import React, { useState, useCallback } from 'react'
import '../App.css'
import {
  Banner,
  Button,
  Overlay,
} from '@ds/ui'

import SideNavBar from './sideNavBar/SideNavBar'
import KeypairManagement from './keyPair/KeyPairManagement'
import NavBar from './layout/NavBar'
import BrandHeader from './layout/BrandHeader'
import RestoreKeys from './restore/RestoreKeys'
import MainPage from './MainPage'
import Footer from './layout/Footer'
import { Theme, InkDocuSignTheme } from '@ds/ui'
import { KeyPairInfo } from '../types/KeyPairInfo';

interface BannerInfo {
  type: 'success' | 'danger'
  message: string
  retryAction?: () => void
  closeAction?: () => void
}

interface ApplianceState {
  message: string
  messageKind: 'success' | 'error' | ''
  bannerInfo: BannerInfo | null
  pendingKeys: string
  namedKeys: string
  view: 'main' | 'keyManagement' | 'restore'
}

export const SecurityApplianceUI: React.FC = () => {
  const [state, setState] = useState<ApplianceState>({
    message: '',
    messageKind: '',
    bannerInfo: null,
    pendingKeys: "0",
    namedKeys: "0",
    view: 'main'
  })

  const [keyPairInfo, setKeyPairInfo] = useState<KeyPairInfo | null>(null);

  const handleBannerUpdate = (bannerInfo: BannerInfo | null) => {
    setState(prev => ({ ...prev, bannerInfo }))
    
    if (bannerInfo && !bannerInfo.retryAction) {
      setTimeout(() => {
        setState(prev => ({ ...prev, bannerInfo: null }))
      }, 10000)
    }

    if (bannerInfo && bannerInfo.retryAction) {
      setTimeout(() => {
        setState(prev => ({ ...prev, bannerInfo: null }))
      }, 30000)
    }
  }

  const handlePendingKeysUpdate = useCallback((newPendingKeys: string) => {
    setState(prevState => ({
      ...prevState,
      pendingKeys: newPendingKeys.toString()
    }))
  }, [])

  const handleNamedKeysUpdate = useCallback((newNamedKeys: string) => {
    setState(prevState => ({
      ...prevState,
      namedKeys: newNamedKeys.toString()
    }))
  }, [])

  const handleManageKeyPair = () => {
    setState(prev => ({ ...prev, view: 'keyManagement' }))
  }

  const handleBackToMain = () => {
    setState(prev => ({ ...prev, view: 'main' }))
  }

  const handleShowRestore = () => {
    setState(prev => ({ ...prev, view: 'restore' }))
  }

  
  return (
    <Theme 
      docuSignTheme={InkDocuSignTheme} 
      enableGlobalCss={true}
      enableNormalizeCss={true}
      enableInkContainerStyles={true}
      enableFontFaceDeclarations={true}
    >
    <div className = "home-container" style={{
      backgroundColor: '#FFFFFF',
      minHeight: '100vh',
      margin: '0 auto',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      <div className = "brand-header" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 600 }}>
        <BrandHeader />
      </div>
      <div className = "main-content" style={{ 
        display: 'flex',
        flex: '1 0 auto',
        position: 'sticky',
        width: '100%',
        maxWidth: '90rem',
        margin: '0 auto',
        backgroundColor: '#FFFFFF',
      }}>
        <div className="side-nav-bar" style={{
          position: 'sticky',
          left: 0,
          top: 0,
          height: '100vh',
          width: '4rem',
          zIndex: 500,
          backgroundColor: '#FFFFFF',
        }}>
          <SideNavBar currentItem={0} expanded={false} />
        </div>
        
        <div className="page-content" style={{ 
          flex: 1,
          display: 'flex',
          paddingLeft: '14rem',
          flexDirection: 'column',
          minHeight: '100%',
          position: 'absolute',
          zIndex: 10,
          marginLeft: '5rem',
        }}>
          <NavBar 
            state={{ view: state.view }}
            setState={(newState: { view: 'main' | 'keyManagement' | 'restore' } | ((prev: { view: 'main' | 'keyManagement' | 'restore' }) => { view: 'main' | 'keyManagement' | 'restore' })) => {
              if (typeof newState === 'function') {
                setState(prev => ({ ...prev, view: newState({ view: prev.view }).view }))
              } else {
                setState(prev => ({ ...prev, view: newState.view }))
              }
            }}
          >
            <div 
              className="page-content-container"
              style={{
                padding: '1.5rem',
                paddingTop: '12rem',
                width: '100%',
                maxWidth: '79.75rem',
                minWidth: '800px',
                flexShrink: 0,
                paddingBottom: '10rem',
              }}>
              {state.bannerInfo && (
                <div style={{
                  position: 'fixed',
                  top: '185px',
                  left: '55.5%',
                  transform: 'translateX(-50%)',
                  zIndex: 99999,
                  width: '70%',
                  maxWidth: '62rem',
                  pointerEvents: 'auto',
                }}>
                  <Banner
                    kind={state.bannerInfo.type}
                    visible
                    closeButton={<Banner.Close onClick={() => {
                      setState(prev => ({ ...prev, bannerInfo: null }))
                    }} />}
                    action={state.bannerInfo.retryAction ? <Banner.Action text='Retry' onClick={state.bannerInfo.retryAction} /> : undefined}
                  >
                    {state.bannerInfo.message}
                  </Banner>
                </div>
              )}
              {state.view === 'restore' && (
                <RestoreKeys onBannerUpdate={handleBannerUpdate} />
              )}
              {state.view === 'keyManagement' && (
                <KeypairManagement 
                  onBack={handleBackToMain}
                  keyPairInfo={keyPairInfo as KeyPairInfo | null}
                  setKeyPairInfo={(info: KeyPairInfo | null) => setKeyPairInfo(info)}
                  onBannerUpdate={handleBannerUpdate} 
                />
              )}
              {state.view === 'main' && (
                <MainPage
                  pendingKeys={state.pendingKeys}
                  namedKeys={state.namedKeys}
                  keyPairInfo={keyPairInfo as KeyPairInfo | null}
                  setKeyPairInfo={(info: KeyPairInfo | null) => setKeyPairInfo(info)}
                  onPendingKeysChange={handlePendingKeysUpdate}
                  onNamedKeysChange={handleNamedKeysUpdate}
                  onBannerUpdate={handleBannerUpdate}
                  onManageKeyPair={handleManageKeyPair}
                  onRestoreKeys={handleShowRestore}
                />
              )}
            </div>
          </NavBar>
        </div>
        <div 
      className="footer-bottom"
        style={{ 
        width: '100%',
        borderTop: '0.0625rem solid #E5E7EB',
        backgroundColor: '#FFFFFF',
        position: 'fixed',
        zIndex: 600,
        marginLeft: 0,
        bottom: 0,
      }}>
          <Footer />
        </div>
      </div>
    </div>
    </Theme>
  )
}

export default SecurityApplianceUI